import { useContext, useState, useEffect } from 'react';
import { AuthContext, defaultAuthValue } from '../contexts/AuthContext';
import { getAuthValueFromToken } from '../utils/tokenHelper';
import { HealthSystemDomainUsersResultBusinessUnitResponse } from '../api/HealthSystemApi';
import { startTokenRefreshWatcher, stopTokenRefreshWatcher } from './tokenRefreshService';
import { loadAndStoreFarmMetadata } from './farmMetadataService';

export default function useAuthContext() {
  const storedToken = window.localStorage.getItem('token') ?? '';
  const storedRefreshToken = window.localStorage.getItem('refreshToken') ?? '';
  const storedBusinessUnits = window.localStorage.getItem('businessUnits') ?? '[]';

  const [refreshToken, setRefreshToken] = useState<string>(storedRefreshToken);
  const [businessUnits, setBusinessUnits] = useState<HealthSystemDomainUsersResultBusinessUnitResponse[]>(
    JSON.parse(storedBusinessUnits)
  );

  const [token, setToken] = useState<string>(storedToken);
  const { state, update } = useContext(AuthContext);

  useEffect(() => {
    if (!token) {
      // Stop token refresh watcher on logout
      stopTokenRefreshWatcher();

      update({
        ...defaultAuthValue,
        isLoggedIn: false,
        selectedBusinessUnitId: '',
        businessUnits: []
      });
      return;
    }

    try {
      const authValue = getAuthValueFromToken(token);
      if (!authValue.isValid) {
        throw new Error('Not valid Token');
      }

      authValue.businessUnits = businessUnits;

      update(authValue);

      window.localStorage.setItem('token', token);
      window.localStorage.setItem('refreshToken', refreshToken);
      window.localStorage.setItem('businessUnits', JSON.stringify(businessUnits));

      if (authValue.farmId) {
        loadAndStoreFarmMetadata(authValue.farmId, token).then(() => {
          const farmMetadataStr = window.localStorage.getItem('farmMetadata');
          const farmName = window.localStorage.getItem('farmName') ?? '';
          const farmTimeZoneId = window.localStorage.getItem('farmTimeZoneId') ?? '';
          if (farmMetadataStr) {
            try {
              const farmMetadata = JSON.parse(farmMetadataStr);
              update((prevState) => ({ ...prevState, farmMetadata, farmName, farmTimeZoneId }));
            } catch (e) {
              console.error('Failed to parse farmMetadata:', e);
            }
          } else {
            update((prevState) => ({ ...prevState, farmName, farmTimeZoneId }));
          }
        });
      }

      // Start background token refresh watcher after successful login
      startTokenRefreshWatcher((newToken, newRefreshToken) => {
        setToken(newToken);
        setRefreshToken(newRefreshToken);
      });
    } catch {
      // not valid / expired / parse error
      stopTokenRefreshWatcher();

      update({
        ...defaultAuthValue,
        isLoggedIn: false,
        token: '',
        isValid: false,
        businessUnits: [],
        selectedBusinessUnitId: ''
      });

      return;
    }
  }, [token, refreshToken, businessUnits]);

  return {
    state,
    updateToken: setToken,
    updateRefreshToken: setRefreshToken,
    updateBusinessUnits: setBusinessUnits,
    updateAuthState: update
  };
}
